import { useTheme } from '../context/ThemeContext';
import { Play, Apple, ChevronDown } from 'lucide-react';

const Hero = ({ onDownloadClick }) => {
  const theme = useTheme();

  return (
    <section 
      id="hero"
      className="min-h-screen flex items-center pt-24 pb-16 relative overflow-hidden"
      style={{ backgroundColor: theme.background }}
    >
      {/* Background gradient blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute -top-40 -right-40 w-96 h-96 rounded-full blur-3xl opacity-20"
          style={{ background: `linear-gradient(135deg, ${theme.gradientStart}, ${theme.gradientEnd})` }}
        />
        <div 
          className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full blur-3xl opacity-10"
          style={{ background: `linear-gradient(135deg, ${theme.gradientEnd}, ${theme.gradientStart})` }}
        />
      </div>

      <div className="container-app relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* Left - Content */}
          <div className="text-center lg:text-left">

            {/* Badge */}
            <div
  className="inline-flex flex-col items-start gap-1 px-4 py-2.5 rounded-2xl mb-6 text-sm font-medium"
  style={{ backgroundColor: theme.cardAlt, color: theme.textSecondary }}
>
  <div className="flex items-center gap-2">
    <span className="w-2 h-2 rounded-full bg-gradient-brand" />
    iOS — Available Now
  </div>
  <div className="flex items-center gap-2">
    <span className="w-2 h-2 rounded-full bg-gradient-brand" />
    Android — Available Now
  </div>
</div>
            {/* Logo */}
            <img
              src="/logo.png"
              alt="TripData"
              className="h-16 md:h-20 object-contain mb-6 mx-auto lg:mx-0"
            />

            {/* Headline */}
            <h1
              className="text-responsive-xl font-bold tracking-tight mb-6"
              style={{ color: theme.text }}
            >
              Track Every Trip,{' '}
              <span className="gradient-text">Effortlessly</span>
            </h1>

            {/* Subtitle */}
            <p
              className="text-lg md:text-xl mb-8 max-w-lg mx-auto lg:mx-0"
              style={{ color: theme.textSecondary }}
            >
              Automatic GPS mileage tracking for business and personal trips.
              Smart logging, powerful analytics, and seamless exports.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
              <button
                onClick={onDownloadClick}
                className="btn btn-gradient gap-2"
              >
                <Apple size={20} />
                App Store
              </button>
              <button
                onClick={onDownloadClick}
                className="btn gap-2 bg-transparent border border-primary text-primary hover:bg-primary hover:text-white transition-all"
              >
                <Play size={20} />
                Google Play
              </button>
            </div>

            {/* Trust note */}
            <p className="text-sm" style={{ color: theme.textMuted }}>
              Free to download &middot; No credit card required &middot;
            </p>

          </div>

          {/* Right - Phone Mockup */}
          <div className="relative flex justify-center lg:justify-end">
            <div className="relative">

              {/* Phone frame */}
              <div
                className="relative z-10 w-64 md:w-72 rounded-[3rem] p-2 shadow-2xl"
                style={{
                  backgroundColor: theme.isDark ? '#1a1a1a' : '#000',
                  boxShadow: theme.isDark
                    ? '0 25px 80px rgba(0,0,0,0.5)'
                    : '0 25px 80px rgba(0,0,0,0.2)',
                }}
              >
                {/* Screen - fixed to hero.png */}
                <div
                  className="rounded-[2.5rem] overflow-hidden aspect-[9/19.5]"
                  style={{ backgroundColor: theme.background }}
                >
                  <img
                    src="/screenshots/hero.png"
                    alt="TripData App"
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                {/* Notch */}
                <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full" />
              </div>

              {/* Float card - km stat */}
              <div
                className="absolute -left-8 top-1/4 p-4 rounded-2xl shadow-xl animate-float z-20"
                style={{
                  backgroundColor: theme.card,
                  animationDelay: '0s',
                  border: `1px solid ${theme.border}`,
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-brand flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs" style={{ color: theme.textMuted }}>This month</p>
                    <p className="text-sm font-semibold" style={{ color: theme.text }}>1,248 km</p>
                  </div>
                </div>
              </div>

              {/* Float card - business trips */}
              <div
                className="absolute -right-4 bottom-1/3 p-4 rounded-2xl shadow-xl animate-float z-20"
                style={{
                  backgroundColor: theme.card,
                  animationDelay: '2s',
                  border: `1px solid ${theme.border}`,
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(255, 152, 0, 0.1)' }}
                  >
                    <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs" style={{ color: theme.textMuted }}>Business trips</p>
                    <p className="text-sm font-semibold" style={{ color: theme.text }}>24 trips</p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2">
          <span className="text-xs" style={{ color: theme.textMuted }}>Scroll to explore</span>
          <ChevronDown className="w-5 h-5 animate-bounce" style={{ color: theme.textMuted }} />
        </div>
      </div>
    </section>
  );
};

export default Hero;